import { QueryClient } from 'react-query';
import { AxiosResponse } from 'axios';
import HttpServiceHelper from './httpServiceHelper';
import { fileUploadApi, getPublicTmaBrandingApi, getTmaBrandingApi, tmaBrandingLogoApi } from '../constants/apiEndPoints';
import { _HttpMethods } from '../constants/staticTypes';

export type _TmaLogoType = 'webLogo' | 'mobileLogo';

// logo fields are public CDN URLs, *PhotoKey fields their S3 keys; an unset logo is null or ''.
// updatedAt / updatedBy are only present once the branding record exists.
export type _TmaBranding = {
    webLogo: string | null;
    webLogoPhotoKey: string | null;
    mobileLogo: string | null;
    mobileLogoPhotoKey: string | null;
    updatedAt?: string;
    updatedBy?: string;
};

type _TmaBrandingPayload = {
    webLogo?: string;
    mobileLogo?: string;
};

export type _SaveTmaBrandingParams = {
    webLogoFile: File | null;
    mobileLogoFile: File | null;
};

export const getTmaBranding = (queryClient: QueryClient) => {
    return HttpServiceHelper({
        url: getTmaBrandingApi,
        method: _HttpMethods.GET,
        queryClient,
    });
};

// public (no login) variant used by the login and password pages
export const getPublicTmaBranding = () => {
    return HttpServiceHelper({
        url: getPublicTmaBrandingApi,
        method: _HttpMethods.GET,
        noAuth: true,
    });
};

// the public response shape is not confirmed yet: accept the logo as a URL string (as the private GET returns it)
// or as an object carrying a url; anything else counts as "not set"
export const extractLogoUrl = (logo: unknown): string | null => {
    if (typeof logo === 'string') return logo || null;
    if (logo && typeof logo === 'object' && 'url' in logo) {
        const url = (logo as { url: unknown }).url;
        return typeof url === 'string' && url ? url : null;
    }
    return null;
};

// uploads the file to the S3 temp folder and resolves with its key (tempFiles/...)
const uploadLogoFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('selectedFile', file);
    const response = await HttpServiceHelper({
        url: fileUploadApi,
        method: _HttpMethods.POST,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        data: formData,
    });
    return response.data.data.fileData.key;
};

// uploads the selected logos, then saves their keys; the backend handles both create and update through POST
export const saveTmaBranding = async ({ webLogoFile, mobileLogoFile }: _SaveTmaBrandingParams): Promise<AxiosResponse> => {
    const [webLogoKey, mobileLogoKey] = await Promise.all([
        webLogoFile ? uploadLogoFile(webLogoFile) : Promise.resolve(null),
        mobileLogoFile ? uploadLogoFile(mobileLogoFile) : Promise.resolve(null),
    ]);

    const data: _TmaBrandingPayload = {};
    if (webLogoKey) data.webLogo = webLogoKey;
    if (mobileLogoKey) data.mobileLogo = mobileLogoKey;

    return HttpServiceHelper({
        url: tmaBrandingLogoApi,
        method: _HttpMethods.POST,
        data,
    });
};

export const deleteTmaBrandingLogo = (logoType: _TmaLogoType): Promise<AxiosResponse> => {
    return HttpServiceHelper({
        url: `${tmaBrandingLogoApi}?logoType=${logoType}`,
        method: _HttpMethods.DELETE,
    });
};
